import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrg, useOrganizations } from '@mysaiva/core/organizations'
import { OrganizationSwitcher } from '@mysaiva/core/components'
import { useApplicationContext } from '@mysaiva/core/application'
import LogoSmall from '../../../assets/logo-small.svg?react'
import { useWorkspaceOrgSource } from '../providers/WorkspaceOrgSourceContext'
import styles from './WorkspaceSidebar.module.scss'

/**
 * Organisation card in the sidebar (avatar, name, partner) that opens the
 * shared organisation switcher. Selection is persisted through core's
 * OrgProvider, so it stays in sync with the product app (UPT or Compass).
 */
export function OrgContextCard() {
  const { t } = useTranslation()
  const { org, select } = useOrg()
  const { apiClient } = useApplicationContext()
  const orgSource = useWorkspaceOrgSource()
  const [searchValue, setSearchValue] = useState('')

  const searchParams = useMemo(() => {
    const params = new URLSearchParams()
    if (searchValue) params.set('org_search', searchValue)
    return params
  }, [searchValue])

  const { data, isLoading, refetch } = useOrganizations({
    productName: orgSource,
    searchParams,
    apiClient
  })

  const organizations = useMemo(
    () => (data?.items ?? []).map(item => ({ id: item.id, name: item.name })),
    [data]
  )

  const label = (
    <span className={styles.orgLabel} data-testid="dp-org-card">
      <span className={styles.orgAvatar} aria-hidden="true">
        <LogoSmall width={18} height={18} />
      </span>
      <span className={styles.orgText}>
        <span className={styles.orgName}>{org.name}</span>
        {org.partner_name && <span className={styles.orgPartner}>{org.partner_name}</span>}
      </span>
    </span>
  )

  return (
    <OrganizationSwitcher
      className={styles.orgCard}
      mode="flat"
      placement="rightTop"
      organizations={organizations}
      selectedOrganization={{ id: org.id, name: org.name }}
      onOrganizationChange={(parentId, organizationId) => select(parentId, organizationId)}
      isLoading={isLoading}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onRefresh={() => refetch()}
      label={label}
      searchPlaceholder={t('developerPlatform.org.search')}
      emptyMessage={t('developerPlatform.org.empty')}
    />
  )
}
